import {
  DEFAULT_ZONE_ID,
  buildMoveMessage,
  balance,
  type ServerMessage,
  type PlayerState,
  type InventorySlot,
  type ResourceNode,
  type WorkshopState,
  type SkillEntry,
} from "./types.ts";
import type { ClientMarketState } from "./market-ui.ts";
import { getOrCreatePlayerId, NetworkClient } from "./network.ts";
import { type BootstrapDeps } from "./resume-handshake.ts";
import { advanceSeq, clearResumeState } from "./resume-storage.ts";
import { applyServerMessage } from "./server-message-reducer.ts";
import { LocalController, RemoteInterpolator } from "./player.ts";
import { Renderer } from "./renderer.ts";
import { InventoryUI } from "./inventory-ui.ts";
import { WorkbenchUI } from "./workbench-ui.ts";
import { NodeRenderer } from "./node-renderer.ts";
import { GatherUI } from "./gather-ui.ts";
import { CreditsHUD } from "./credits-hud.ts";
import { MarketUI } from "./market-ui.ts";
import { buildNexusDebug } from "./nexus-debug.ts";
import { attachHudHints } from "./hud-hints.ts";
import { attachInputRouter } from "./input-router.ts";
import { CombatUI } from "./combat-ui.ts";
import {
  BASIC_ATTACK_ABILITY,
  createCombatState,
  setTarget,
  SLOT_LOADOUT,
  tickFeedback,
  type CombatClientState,
} from "./combat-state.ts";
import { MobRenderer } from "./mob-renderer.ts";
import { resolveEntityPosition } from "./entity-position-resolver.ts";
import { PartyUI, ToastOverlay } from "./party-ui.ts";
import {
  createPartyState,
  removePendingInvite,
  tickToasts,
  type PartyClientState,
} from "./party-state.ts";
import { attachExpeditionReadyUI } from "./expedition-ready-ui.ts";
import {
  createExpeditionState,
  loadDismissedInvites,
  type ExpeditionClientState,
} from "./expedition-state.ts";
import { buildUseAbilityMessage } from "../../shared/combat-messages.ts";
import { GalaxyMapUI, TravelOverlay } from "./galaxy-map-ui.ts";
import { PlayerStatsUI, showTierToast } from "./player-stats-ui.ts";
import { createTravelState } from "./travel-state.ts";
import { STATIONS } from "../../shared/stations.ts";
import { DisplayNameModal } from "./display-name-modal.ts";
import { InventoryModal, INVENTORY_MODAL_REFRESH_TYPES } from "./inventory-modal.ts";
import { nearestGatherableNode } from "./nearest-node.ts";
import { EventLog } from "./event-log.ts";
import type { EquippedItemReadout } from "../../shared/equip-messages.ts";
import { FriendsUI } from "./friends-ui.ts";
import { buildRequestNearbyPlayersMessage } from "../../shared/friends-messages.ts";

async function boot(): Promise<void> {
  const mount = document.getElementById("game");
  if (!mount) throw new Error("#game mount point missing");
  // Phase 7 Block A finding 5.2 — suppress browser context menu in #game.
  mount.addEventListener("contextmenu", (e) => e.preventDefault());
  const renderer = new Renderer();
  await renderer.init(mount);
  const inventoryUI = new InventoryUI(renderer.app);
  const workbenchUI = new WorkbenchUI(renderer.app);
  const nodeRenderer = new NodeRenderer(renderer.worldContainer);
  const gatherUI = new GatherUI(renderer.worldContainer);
  const creditsHud = new CreditsHUD(renderer.app);
  const marketUI = new MarketUI(renderer.app);
  const playerId = getOrCreatePlayerId();
  const zoneId = DEFAULT_ZONE_ID;
  renderer.setLocalPlayerId(playerId);
  const wsUrl = import.meta.env.VITE_WS_URL ?? "ws://localhost:8787/ws";
  const network = new NetworkClient(wsUrl, playerId, zoneId);
  workbenchUI.sendFn = (msg) => network.send(msg);
  // Phase 7 Block D — workbench reads inventory through this getter so
  // the materials gate + info-pane have/need rows always reflect the
  // freshest snapshot (closure resolves at every action-bar render).
  workbenchUI.setInventoryProvider(() => clientState.inventory);
  // MarketUI wiring — sendFn for the Buy/List/Cancel click-paths,
  // getters close over clientState so the panel always renders the
  // latest snapshot, inventoryRef lets the panel drive selection mode.
  marketUI.setHost({
    send: (msg) => network.send(msg),
    selfId: () => clientState.self?.playerId ?? null,
    inventory: () => clientState.inventory,
    credits: () => clientState.credits,
    market: () => clientState.market,
    skills: () => clientState.skills,
    inventoryUI,
    selfPlayer: () => clientState.self,
    zonePlayers: () => clientState.players,
  });
  inventoryUI.setSkillXpProvider((skillId) => clientState.skills.find((s) => s.skillId === skillId)?.xp ?? 0);
  inventoryUI.setEquippedRowidProvider(() => clientState.equippedItem?.inventoryRowid ?? null);
  const interp = new RemoteInterpolator();
  const clientState = {
    self: null as PlayerState | null,
    players: new Map<string, PlayerState>(),
    inventory: [] as InventorySlot[],
    activeGather: null as { nodeId: string; startedAt: number; expectedCompleteAt: number } | null,
    nodes: new Map<string, ResourceNode>(),
    currentZoneId: null as string | null, // Phase 5 Step 5: zone-change detection
    workshop: { jobs: [], completedUnclaimed: [] } as WorkshopState,
    skills: [] as SkillEntry[],
    credits: 0,
    market: { listings: [], npcInventory: {}, npcPrices: [], fees: 0, priceHistories: {}, remoteHistories: {} } as ClientMarketState,
    // Phase 5 Step 15b — combat client state: targetId, cooldowns,
    // selfHp, mobHp/playerHp maps, floating feedback queue.
    combat: createCombatState(balance.combat.defaultPlayerMaxHp) as CombatClientState,
    // Phase 5 Step 16b — party client state: pending invites, roster,
    // toast queue for partyInvite arrivals.
    party: createPartyState() as PartyClientState,
    // Phase 5 Step 17 — expedition entry modal + completion modal
    // state. dismissedInvites hydrated from localStorage so the durable
    // expeditionReady envelope re-replayed on reconnect is skipped.
    expedition: { ...createExpeditionState(), dismissedInvites: loadDismissedInvites(window.localStorage) } as ExpeditionClientState,
    travel: createTravelState(),
    equippedItem: null as EquippedItemReadout | null, // Phase 7 Block C — hydrated from snapshot
  };

  const combatUI = new CombatUI(renderer.app, renderer.worldContainer);
  const friendsUI = new FriendsUI({ send: (m) => network.send(m), onOpen: () => local.setModal(true), onClose: () => local.setModal(false) });
  const partyUI = new PartyUI({
    app: renderer.app,
    selfId: () => clientState.self?.playerId ?? playerId,
    send: (msg) => network.send(msg),
    onOpen: () => local.setModal(true),
    onClose: () => local.setModal(false),
    selfPlayer: () => clientState.self,
    zonePlayers: () => clientState.players,
    onRequestNearbyPlayers: () => network.send(buildRequestNearbyPlayersMessage()),
  });
  partyUI.onDeclineHook = (partyId) => {
    removePendingInvite(clientState.party, partyId);
    partyUI.refresh(clientState.party);
  };
  const toastOverlay = new ToastOverlay({
    app: renderer.app,
    onToastClick: (contextId) => {
      if (!partyUI.isOpen) partyUI.open(clientState.party);
      void contextId;
    },
  });
  const expeditionReadyUI = attachExpeditionReadyUI({
    app: renderer.app,
    send: (msg) => network.send(msg),
    setModal: (open) => local.setModal(open),
    expedition: clientState.expedition,
    storage: window.localStorage,
    // Phase 7 Block A finding 8.5 fallback — see expedition-ready-ui.
    forceReconnectAfterAccept: () => { clearResumeState(window.localStorage); network.reconnect(); },
  });
  const travelOverlay = new TravelOverlay();
  const displayNameModal = new DisplayNameModal({ send: (m) => network.send(m), setModalSuppressed: (o) => local.setModal(o) });
  const inventoryModal = new InventoryModal({
    send: (m) => network.send(m),
    getInventory: () => clientState.inventory,
    getEquipped: () => clientState.equippedItem,
    getSkillXp: (skillId) => clientState.skills.find((s) => s.skillId === skillId)?.xp ?? 0,
    setModalSuppressed: (o) => local.setModal(o),
  });
  const playerStatsUI = new PlayerStatsUI({
    getDisplayName: () => clientState.self?.displayName ?? "",
    onChangeName: () => displayNameModal.open(clientState.self?.displayName ?? ""),
  });
  const galaxyMapUI = new GalaxyMapUI({
    send: (msg) => network.send(msg),
    getCurrentStationId: () => {
      const zoneId = clientState.currentZoneId;
      if (!zoneId) return "station-7";
      return zoneId.split(":")[0] ?? "station-7";
    },
    isTraveling: () => clientState.travel.travelingTo !== null,
  });
  const mobRenderer = new MobRenderer({
    worldContainer: renderer.worldContainer,
    onTargetClicked: (mobId) => setTarget(clientState.combat, mobId),
  });

  // __nexusDebug surface for Phase 4 browser tests; gated on DEV/?test (DECISIONS 2026-04-18 Step 2).
  const debugEnabled = import.meta.env.DEV || new URLSearchParams(window.location.search).has("test");
  if (debugEnabled) {
    window.__nexusDebug = buildNexusDebug(
      {
        self: () => clientState.self,
        players: () => clientState.players.values(),
        inventory: () => clientState.inventory,
        activeGather: () => clientState.activeGather,
        nodes: () => clientState.nodes.values(),
        workshop: () => clientState.workshop,
        skills: () => clientState.skills,
        credits: () => clientState.credits,
        market: () => clientState.market,
        marketUI,
        workbenchUI,
        partyUI,
        app: renderer.app,
        currentZoneId: () => clientState.currentZoneId,
        combat: () => clientState.combat,
        party: () => clientState.party,
        expedition: () => clientState.expedition,
        equippedItem: () => clientState.equippedItem,
      },
      {
        send: (msg) => network.send(msg),
        reconnect: () => network.reconnect(),
        updateZoneId: (zoneId: string) => network.updateZoneId(zoneId),
        combatState: clientState.combat,
        expeditionState: clientState.expedition,
        storage: window.localStorage,
      },
    );
  }

  const local = new LocalController((x, y, t) => {
    network.send(buildMoveMessage(x, y, t));
  });
  local.attach(window);

  // Phase 5 Step 19 — workshop/market polling retired; updates arrive via durable pushes.
  // Keydown/keyup routing extracted to input-router.ts (Phase 4 Step 20).
  attachInputRouter({
    target: window,
    workbenchUI,
    marketUI,
    local,
    network,
    getActiveGather: () => clientState.activeGather,
    getSkills: () => clientState.skills,
    getWorkshop: () => clientState.workshop,
    findNearestGatherableNode: () => nearestGatherableNode(clientState.nodes, local.x, local.y),
    onAbilitySlot: (slotIdx) => {
      const abilityId = SLOT_LOADOUT[slotIdx];
      if (abilityId === undefined) return;
      const targetId = clientState.combat.targetId;
      if (targetId === null) return;
      network.send(buildUseAbilityMessage(abilityId, targetId));
    },
    onBasicAttack: () => {
      const targetId = clientState.combat.targetId;
      if (targetId === null) return;
      network.send(buildUseAbilityMessage(BASIC_ATTACK_ABILITY, targetId));
    },
    onPartyToggle: () => {
      if (partyUI.isOpen) partyUI.close();
      else partyUI.open(clientState.party);
    },
    getPartyOpen: () => partyUI.isOpen,
    onExpeditionModalEscape: () => expeditionReadyUI.handleEscape(),
    getExpeditionModalOpen: () => expeditionReadyUI.isOpen,
    onGalaxyMapToggle: () => galaxyMapUI.toggle(),
    getGalaxyMapOpen: () => galaxyMapUI.isOpen,
    onStatsToggle: () => playerStatsUI.toggle(clientState.skills),
    getStatsOpen: () => playerStatsUI.isOpen,
    getDisplayNameModalOpen: () => displayNameModal.isOpen,
    onInventoryToggle: () => inventoryModal.toggle(),
    getInventoryModalOpen: () => inventoryModal.isOpen,
    onFriendsToggle: () => friendsUI.toggle(),
    getFriendsOpen: () => friendsUI.isOpen,
  });

  const eventLog = new EventLog();
  const bootstrapDeps = (now: number): BootstrapDeps => ({
    localPlayerId: playerId,
    now,
    clientState,
    renderer,
    interp,
    local,
    inventoryUI,
    nodeRenderer,
    creditsHud,
  });

  network.onMessage((msg: ServerMessage) => {
    displayNameModal.applyServerMessage(msg, playerId); // Phase 7 Block B
    applyServerMessage(msg, {
      localPlayerId: playerId,
      clientState,
      renderer,
      interp,
      inventoryUI,
      workbenchUI,
      nodeRenderer,
      gatherUI,
      mobRenderer,
      marketDeps: { clientState, creditsHud, marketUI, inventoryUI },
      localStorage: window.localStorage,
      bootstrapDeps,
      onExpeditionAccepted: () => network.reconnect(),
      onJumpCompleted: (stationId) => {
        travelOverlay.hide();
        // The zoneId for the new station is "{stationId}:hub".
        const station = STATIONS[stationId];
        if (station) network.updateZoneId(station.zoneId);
        network.reconnect();
      },
      onSkillTierReached: showTierToast,
      eventLog,
      onNearbyPlayers: (players) => partyUI.applyNearbyPlayers(players),
      onFriendsListUpdated: (friends) => friendsUI.applyFriendsList(friends),
    });
    // Show/hide travel overlay based on travel state changes.
    const { travelingTo, completesAt } = clientState.travel;
    if (travelingTo !== null && completesAt !== null) {
      if (!travelOverlay.isShowing) {
        const st = STATIONS[travelingTo];
        travelOverlay.show(st ? st.displayName : travelingTo, completesAt);
      }
    } else {
      travelOverlay.hide();
    }
    const refreshIfRelevant = (m: ServerMessage): void => {
      if (inventoryModal.isOpen && INVENTORY_MODAL_REFRESH_TYPES.has(m.type)) inventoryModal.refresh();
    };
    if (msg.type === "replay") {
      for (const envelope of msg.payload.envelopes) {
        if (typeof envelope.seq === "number") advanceSeq(window.localStorage, envelope.seq);
        refreshIfRelevant(envelope);
      }
      return;
    }
    if (typeof msg.seq === "number") advanceSeq(window.localStorage, msg.seq);
    refreshIfRelevant(msg);
  });

  attachHudHints(playerId);
  network.connect();

  // Frame loop: advance local prediction, sample interpolation for every
  // remote, then update camera. Pixi drives this via its ticker so the
  // cadence matches the render rate.
  renderer.app.ticker.add((ticker) => {
    const now = performance.now();
    const dtSeconds = ticker.deltaMS / 1000; // contracts:allow-literal — ms->s unit conversion

    const prevX = local.x;
    const prevY = local.y;
    local.tick(dtSeconds, now);

    if (clientState.self) {
      clientState.self.x = local.x;
      clientState.self.y = local.y;
    }

    if ((local.x !== prevX || local.y !== prevY) && clientState.activeGather) {
      clientState.activeGather = null;
      gatherUI.stop();
    }

    renderer.updatePlayer(playerId, local.x, local.y);
    gatherUI.update(local.x, local.y);

    for (const id of renderer.playerIds()) {
      if (id === playerId) continue;
      const sample = interp.sample(id, now);
      if (sample) renderer.updatePlayer(id, sample.x, sample.y);
    }

    tickFeedback(clientState.combat, now);
    tickToasts(clientState.party, now);
    toastOverlay.tick(clientState.party.toasts, now);
    if (partyUI.isOpen) partyUI.refresh(clientState.party);
    expeditionReadyUI.sync(clientState.expedition.pendingReady, clientState.expedition.completed);
    mobRenderer.sync(clientState.combat.mobPositions);
    combatUI.tick(
      clientState.combat,
      (entityId) =>
        resolveEntityPosition(
          {
            localPlayer: { id: playerId, x: local.x, y: local.y },
            expeditionPeers: clientState.combat.playerPositions,
            zonePlayers: clientState.players,
            mobPositions: clientState.combat.mobPositions,
          },
          entityId,
        ),
      now,
    );

    renderer.updateCamera();
  });
}

boot().catch((err) => {
  console.error("nexus-forge boot failed", err);
});
